import type { EditControlProps } from '../../declare'
import { useState, useEffect, useRef, useContext } from 'react'
import { Cascader } from 'antd'
import { ListTableContext, EditContext } from 'src/components'
import { appStore } from 'src/stores'
import { $api } from 'src/plugins'
import {
  getNavFieldsFromNavField,
  getRulesFromNavFilters,
  getSaveFieldFromNavField,
  generateCascaderTreeData,
  generateFlatTreeData
} from 'src/components/root/helper'
import { initFilter, getSingleRule, extendRules, extendInclude } from 'src/utils/common'
import { get, cloneDeep } from 'lodash'

const CascaderSelect = (props: EditControlProps) => {
  const table = useContext(ListTableContext)
  const edit = useContext(EditContext)

  // 检查节点是否允许选中
  const checkNodeSelectable = (node: any, condition: string) => {
    // condition: 'FIELD,OPERATE,VALUE'
    if (condition) {
      const configs: any[] = []
      const codes = condition.split('|')
      codes.forEach(code => {
        const splitArr = code.split(',')
        configs.push({
          field: splitArr[0],
          operate: Number(splitArr[1]),
          value: splitArr[2]
        })
      })
      return configs.find(conf => {
        let matchResult = false
        const { field, operate, value } = conf
        if (field) {
          const fieldValue = get(node, field)
          switch (operate) {
            case 1:
              matchResult = fieldValue === value
              break
            default:
              break
          }
        }
        return matchResult
      })
    }
    return true
  }

  // 树下拉数据
  const [treeData, setTreeData] = useState<any[]>([])
  const sourceData = useRef<any[]>([])
  const onLoadData = (params?: any) => {
    const {
      INPUT_DATA_API: url,
      INPUT_DATA_LEVEL,
      INPUT_FILTER_ORG_TYPE: OrgType,
      INPUT_NAV_FIELD,
      Nav_Filters,
      IS_ONLY_CHECK_LEAF,
      SELECT_CONDITION
    } = props.config
    if (!url) return new Promise(resolve => { resolve(false) })

    const { OrgId } = appStore.current.headers
    const payload: any = initFilter({ OrgId, OrgType })
    payload.Level = params?.id ? 1 : (INPUT_DATA_LEVEL || 0)
    // 搜索过滤字段
    const singleRule = []
    params?.id && singleRule.push(getSingleRule('PARENT_ID', 1, params.id))
    // 菜单参数
    payload.MenuParameter = appStore.current.activeMenu.MENU_FORM_PARAMS
    // navFields 导航字段
    const navFields = getNavFieldsFromNavField(INPUT_NAV_FIELD)
    navFields.length && extendInclude(payload, navFields)
    // 配置的过滤字段
    const filters = getRulesFromNavFilters(Nav_Filters, {
      user: appStore.current.userinfo,
      formBase: edit.formBase,
      record: table?.selectedRow
    })
    extendRules(payload, singleRule.concat(filters))

    return new Promise((resolve) => {
      $api['app/getDataByPost'](payload, {
        url
      }).then((res: any) => {
        if (res) {
          // 一次性加载全部数据
          if (INPUT_DATA_LEVEL === -1) {
            const simpleData: any[] = []
            generateCascaderTreeData(res, simpleData)
            generateFlatTreeData(res, sourceData.current)
            setTreeData(simpleData)
            return
          }
          const updateValue = props.value ? props.value.split(',').filter((val: string) => val !== params?.id) : []
          // 新数据源
          const data = cloneDeep(treeData)
          res.forEach((item: any) => {
            const pLevel = params?.Level || 0
            const isLeaf = item.IsLeaf || !!(INPUT_DATA_LEVEL && INPUT_DATA_LEVEL >= 0 && (pLevel + 1 >= INPUT_DATA_LEVEL))
            // 判断树节点是否可选中
            const conditionSelectable = checkNodeSelectable(item, SELECT_CONDITION)
            const disabled = !conditionSelectable || (IS_ONLY_CHECK_LEAF && !isLeaf)
            data.push({
              label: item.Node.NAME,
              value: item.Node.ID,
              isLeaf,
              disabled,
            })
            sourceData.current.push(item.Node)
            // 构建需要更新的 value 值
            updateValue.push(item.Node.ID)
          })
          // 如果继续加载发现该节点没有子节点了，那么这个节点就设置为子节点
          if (!res.length && !params?.isLeaf) {
            const findNode = data.find(item => item.id === params?.id)
            if (findNode) {
              findNode.isLeaf = true
              findNode.disabled = false
            }
          }
          setTreeData(data)
        }
      }).finally(() => {
        resolve(undefined)
      })
    })
  }
  useEffect(() => { onLoadData() }, [])

  const handleChange = (value: any) => {
    if (!value?.length) {
      const extraFields = getSaveFieldFromNavField('', sourceData.current, props.config)
      props.onChange?.('', extraFields)
      return
    }
    const extraFields = getSaveFieldFromNavField(value, sourceData.current, props.config)
    props.onChange?.(value.join(','), extraFields)
  }

  const treeValue = props.value ? props.value.split(',') : undefined

  const { __READONLY__, EDITABLE, CHECK_STRICTLY, INPUT_DATA_LEVEL, PROMPT_INFO } = props.config
  return (
    <Cascader
      allowClear
      changeOnSelect={CHECK_STRICTLY}
      disabled={__READONLY__ || !EDITABLE || props.evtConfig?.disabled}
      value={treeValue}
      options={treeData}
      placeholder={PROMPT_INFO}
      loadData={INPUT_DATA_LEVEL !== -1 ? onLoadData : undefined}
      onChange={handleChange}
    />
  )
}

export default CascaderSelect